/* Copy da área Empresas (companies/): lista + detalhe (overview, membros, packages, auditoria,
   assinatura). Os modais (create/block/unblock/assign-plan) e o catálogo de erros moram em
   `companies-modals.ts` só por causa do limite de 500 LOC (§4.7); `useCompaniesContent()` funde
   os dois para quem consome — a área continua sendo UM hook, não dois. */
import type { CompanyStatus } from '@planuze/admin-contracts';
import type { Bilingual } from '../bilingual.js';
import { useBilingual } from '../locale-provider.js';
import {
  COMPANIES_MODALS_CONTENT,
  type CompaniesModalsContent,
  type CompanyErrorKey,
} from './companies-modals.js';

export type { CompanyErrorKey };

interface CompaniesListContent {
  readonly common: {
    readonly members: string;
    readonly createdAt: string;
    readonly noResultsTitle: string;
    readonly clearFilters: string;
    readonly countOf: (shown: number, total: number) => string;
  };
  readonly filters: {
    readonly searchLabel: string;
    readonly searchPlaceholder: string;
    readonly statusFilterLabel: string;
    readonly statusPlaceholder: string;
    readonly statusAll: string;
  };
  readonly status: Readonly<Record<CompanyStatus, string>>;
  readonly table: {
    readonly columnCompany: string;
    readonly columnStatus: string;
    readonly columnMembers: string;
    readonly columnCreatedAt: string;
    readonly emptyTitle: string;
    readonly emptyDescription: string;
    readonly emptyFilteredDescription: string;
    readonly selectPageAriaLabel: string;
    readonly selectRowAriaLabel: (name: string) => string;
  };
  readonly detailHeader: {
    readonly slugTitle: string;
    readonly copyIdTitle: string;
    readonly copySuccess: string;
    readonly copyError: string;
    readonly unblock: string;
    readonly block: string;
    readonly summarySeats: string;
    readonly summarySubscription: string;
    readonly summaryNoSubscription: string;
  };
  readonly overviewTab: {
    readonly deletionRequestedTitle: string;
    readonly deletionRequestedDescription: (date: string) => string;
    readonly owner: string;
    readonly ownerSince: (date: string) => string;
    readonly legalName: string;
    readonly taxId: string;
    readonly publisher: string;
    readonly blockedAt: string;
    readonly blockedReason: string;
    readonly roles: string;
  };
  readonly membersTab: {
    readonly searchLabel: string;
    readonly searchPlaceholder: string;
    readonly roleFacetLabel: string;
    readonly empty: string;
    readonly emptyFilteredDescription: string;
    readonly columnEmail: string;
    readonly columnRole: string;
    readonly columnJoinedAt: string;
  };
  readonly packagesTab: {
    readonly searchLabel: string;
    readonly searchPlaceholder: string;
    readonly verifiedFacetLabel: string;
    readonly activeFacetLabel: string;
    readonly optionAll: string;
    readonly optionVerified: string;
    readonly optionActive: string;
    readonly loadError: string;
    readonly emptyTitle: string;
    readonly emptyDescription: string;
    readonly emptyFilteredDescription: string;
    readonly verifiedBadge: string;
    readonly noActiveVersionBadge: string;
    readonly columnPackage: string;
    readonly columnAuthor: string;
    readonly columnActiveVersion: string;
    readonly columnVersions: string;
    readonly columnUpdatedAt: string;
  };
  readonly auditTab: {
    readonly viewFullAudit: string;
    readonly actionTypeLabel: string;
    readonly emptyTitle: string;
    readonly emptyFilteredDescription: string;
  };
  readonly subscriptionTab: {
    readonly assignPlan: string;
    readonly emptyTitle: string;
    readonly emptyDescription: string;
    readonly cancelScheduledTitle: string;
    readonly cancelScheduledDescription: (date: string) => string;
    readonly planChangeScheduledTitle: string;
    readonly planChangeTo: string;
    readonly planChangeSuffix: (date: string | null) => string;
    readonly status: string;
    readonly plan: string;
    readonly tier: string;
    readonly price: string;
    readonly periodStart: string;
    readonly periodEnd: string;
  };
}

export type CompaniesContent = CompaniesListContent & CompaniesModalsContent;

const COMPANIES_LIST_CONTENT: Bilingual<CompaniesListContent> = {
  'pt-BR': {
    common: {
      members: 'Membros',
      createdAt: 'Criada em',
      noResultsTitle: 'Nenhum resultado',
      clearFilters: 'Limpar filtros',
      countOf: (shown, total) => `${shown} de ${total}`,
    },
    filters: {
      searchLabel: 'Buscar empresas',
      searchPlaceholder: 'Buscar por nome…',
      statusFilterLabel: 'Filtrar por status',
      statusPlaceholder: 'Status',
      statusAll: 'Todos',
    },
    status: { active: 'Ativa', trial: 'Trial', blocked: 'Bloqueada' },
    table: {
      columnCompany: 'Empresa',
      columnStatus: 'Status',
      columnMembers: 'Membros',
      columnCreatedAt: 'Criada em',
      emptyTitle: 'Nenhuma empresa',
      emptyDescription: 'As empresas cadastradas aparecem aqui.',
      emptyFilteredDescription: 'Nenhuma empresa corresponde à busca ou aos filtros.',
      selectPageAriaLabel: 'Selecionar empresas visíveis',
      selectRowAriaLabel: (name) => `Selecionar ${name}`,
    },
    detailHeader: {
      slugTitle: 'Slug da empresa',
      copyIdTitle: 'Copiar ID',
      copySuccess: 'ID copiado.',
      copyError: 'Não foi possível copiar o ID.',
      unblock: 'Desbloquear',
      block: 'Bloquear',
      summarySeats: 'Assentos',
      summarySubscription: 'Assinatura',
      summaryNoSubscription: 'Sem assinatura',
    },
    overviewTab: {
      deletionRequestedTitle: 'Exclusão solicitada',
      deletionRequestedDescription: (date) =>
        `A empresa está em período de graça desde ${date} e ainda pode ser restaurada.`,
      owner: 'Owner',
      ownerSince: (date) => `Desde ${date}`,
      legalName: 'Razão social',
      taxId: 'CNPJ',
      publisher: 'Publisher',
      blockedAt: 'Bloqueada em',
      blockedReason: 'Motivo do bloqueio',
      roles: 'Papéis',
    },
    membersTab: {
      searchLabel: 'Buscar membros por email',
      searchPlaceholder: 'Buscar por email…',
      roleFacetLabel: 'Papel',
      empty: 'Sem membros',
      emptyFilteredDescription: 'Nenhum membro corresponde à busca ou ao filtro de papel.',
      columnEmail: 'Email',
      columnRole: 'Papel',
      columnJoinedAt: 'Entrou em',
    },
    packagesTab: {
      searchLabel: 'Buscar packages por nome ou id',
      searchPlaceholder: 'Buscar por nome ou id…',
      verifiedFacetLabel: 'Filtrar por verificação',
      activeFacetLabel: 'Filtrar por versão ativa',
      optionAll: 'Todos',
      optionVerified: 'Verificados',
      optionActive: 'Com versão ativa',
      loadError: 'Falha ao carregar os packages da empresa.',
      emptyTitle: 'Nenhum package privado',
      emptyDescription: 'Packages publicados como org-privada para esta empresa aparecem aqui.',
      emptyFilteredDescription: 'Nenhum package corresponde à busca ou aos filtros.',
      verifiedBadge: 'Verificado',
      noActiveVersionBadge: 'Sem versão ativa',
      columnPackage: 'Package',
      columnAuthor: 'Autor',
      columnActiveVersion: 'Versão ativa',
      columnVersions: 'Versões',
      columnUpdatedAt: 'Atualizado',
    },
    auditTab: {
      viewFullAudit: 'Ver auditoria completa',
      actionTypeLabel: 'Tipo de ação',
      emptyTitle: 'Sem ações registradas',
      emptyFilteredDescription: 'Nenhuma ação corresponde ao filtro selecionado.',
    },
    subscriptionTab: {
      assignPlan: 'Atribuir plano',
      emptyTitle: 'Sem assinatura',
      emptyDescription: 'Nenhuma assinatura ativa ou billing indisponível no momento.',
      cancelScheduledTitle: 'Cancelamento agendado',
      cancelScheduledDescription: (date) => `A assinatura será cancelada em ${date}.`,
      planChangeScheduledTitle: 'Mudança de plano agendada',
      planChangeTo: 'Troca para',
      planChangeSuffix: (date) => (date !== null ? ` em ${date}.` : '.'),
      status: 'Status',
      plan: 'Plano',
      tier: 'Tier',
      price: 'Preço',
      periodStart: 'Início do período',
      periodEnd: 'Fim do período',
    },
  },
  'en-US': {
    common: {
      members: 'Members',
      createdAt: 'Created at',
      noResultsTitle: 'No results',
      clearFilters: 'Clear filters',
      countOf: (shown, total) => `${shown} of ${total}`,
    },
    filters: {
      searchLabel: 'Search companies',
      searchPlaceholder: 'Search by name…',
      statusFilterLabel: 'Filter by status',
      statusPlaceholder: 'Status',
      statusAll: 'All',
    },
    status: { active: 'Active', trial: 'Trial', blocked: 'Blocked' },
    table: {
      columnCompany: 'Company',
      columnStatus: 'Status',
      columnMembers: 'Members',
      columnCreatedAt: 'Created at',
      emptyTitle: 'No companies',
      emptyDescription: 'Registered companies appear here.',
      emptyFilteredDescription: 'No company matches the search or filters.',
      selectPageAriaLabel: 'Select visible companies',
      selectRowAriaLabel: (name) => `Select ${name}`,
    },
    detailHeader: {
      slugTitle: 'Company slug',
      copyIdTitle: 'Copy ID',
      copySuccess: 'ID copied.',
      copyError: 'Could not copy the ID.',
      unblock: 'Unblock',
      block: 'Block',
      summarySeats: 'Seats',
      summarySubscription: 'Subscription',
      summaryNoSubscription: 'No subscription',
    },
    overviewTab: {
      deletionRequestedTitle: 'Deletion requested',
      deletionRequestedDescription: (date) =>
        `The company has been in the grace period since ${date} and can still be restored.`,
      owner: 'Owner',
      ownerSince: (date) => `Since ${date}`,
      legalName: 'Legal name',
      taxId: 'Tax ID',
      publisher: 'Publisher',
      blockedAt: 'Blocked at',
      blockedReason: 'Block reason',
      roles: 'Roles',
    },
    membersTab: {
      searchLabel: 'Search members by email',
      searchPlaceholder: 'Search by email…',
      roleFacetLabel: 'Role',
      empty: 'No members',
      emptyFilteredDescription: 'No member matches the search or role filter.',
      columnEmail: 'Email',
      columnRole: 'Role',
      columnJoinedAt: 'Joined at',
    },
    packagesTab: {
      searchLabel: 'Search packages by name or id',
      searchPlaceholder: 'Search by name or id…',
      verifiedFacetLabel: 'Filter by verification',
      activeFacetLabel: 'Filter by active version',
      optionAll: 'All',
      optionVerified: 'Verified',
      optionActive: 'With active version',
      loadError: "Failed to load the company's packages.",
      emptyTitle: 'No private packages',
      emptyDescription: 'Packages published as org-private for this company appear here.',
      emptyFilteredDescription: 'No package matches the search or filters.',
      verifiedBadge: 'Verified',
      noActiveVersionBadge: 'No active version',
      columnPackage: 'Package',
      columnAuthor: 'Author',
      columnActiveVersion: 'Active version',
      columnVersions: 'Versions',
      columnUpdatedAt: 'Updated',
    },
    auditTab: {
      viewFullAudit: 'View full audit trail',
      actionTypeLabel: 'Action type',
      emptyTitle: 'No actions recorded',
      emptyFilteredDescription: 'No action matches the selected filter.',
    },
    subscriptionTab: {
      assignPlan: 'Assign plan',
      emptyTitle: 'No subscription',
      emptyDescription: 'No active subscription, or billing is unavailable right now.',
      cancelScheduledTitle: 'Cancellation scheduled',
      cancelScheduledDescription: (date) => `The subscription will be canceled on ${date}.`,
      planChangeScheduledTitle: 'Plan change scheduled',
      planChangeTo: 'Switching to',
      planChangeSuffix: (date) => (date !== null ? ` on ${date}.` : '.'),
      status: 'Status',
      plan: 'Plan',
      tier: 'Tier',
      price: 'Price',
      periodStart: 'Period start',
      periodEnd: 'Period end',
    },
  },
};

const COMPANIES_CONTENT: Bilingual<CompaniesContent> = {
  'pt-BR': { ...COMPANIES_LIST_CONTENT['pt-BR'], ...COMPANIES_MODALS_CONTENT['pt-BR'] },
  'en-US': { ...COMPANIES_LIST_CONTENT['en-US'], ...COMPANIES_MODALS_CONTENT['en-US'] },
};

export const useCompaniesContent = (): CompaniesContent => useBilingual(COMPANIES_CONTENT);

/** Exportado para os defaults pt-BR de `company-error.ts`/`company-status.ts` (chamadores fora
    de componente não têm acesso ao hook). */
export { COMPANIES_CONTENT };
